import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Home, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8" data-testid="logo-link">
            <div className="w-12 h-12 bg-[#D97757] rounded-full flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-semibold text-2xl text-[#2C3329]">NeighborBorrow</span>
          </Link>
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-8">
            <div className="w-14 h-14 bg-[#E53935]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-[#E53935]" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">Invalid link</h1>
            <p className="text-[#596652] mb-6">
              This password reset link is invalid or missing. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="text-[#D97757] hover:text-[#C56547] font-medium"
              data-testid="request-new-link"
            >
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center space-x-2 mb-8" data-testid="logo-link">
          <div className="w-12 h-12 bg-[#D97757] rounded-full flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-semibold text-2xl text-[#2C3329]">NeighborBorrow</span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#EBE8DF] p-8">
          {success ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-16 h-16 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#4CAF50]" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
                Password reset!
              </h1>
              <p className="text-[#596652] mb-6">
                Your password has been updated. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547] px-8"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-semibold text-[#2C3329] text-center mb-2">
                Set new password
              </h1>
              <p className="text-[#596652] text-center text-sm mb-6">
                Choose a strong password with at least 6 characters.
              </p>

              {error && (
                <div className="bg-[#E53935]/10 text-[#E53935] px-4 py-3 rounded-xl mb-4 text-sm" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password" className="text-[#2C3329]">New password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      required
                      className="border-[#EBE8DF] rounded-xl pr-10 focus:ring-2 focus:ring-[#8C9A76]/50"
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#596652] hover:text-[#2C3329]"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-[#2C3329]">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    required
                    className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="••••••••"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547] py-3"
                  data-testid="reset-password-submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
